import { useMemo } from "react";
import Artist from "../store/models/artist";
import Engineer from "../store/models/engineer";
import { useAppSelector } from "../store/hooks";
import { selectReviews } from "../store/selectors/reviewsSelector";
import { Review, ReviewState } from "../store/models/review";
import { Studio, StudioRoom } from "../store/models/studio";

export const useStudioRoomReviewStats = (
  studioRoomId?: number,
): ReviewState | undefined => {
  const reviewStore = useAppSelector(selectReviews);
  return useMemo(() => {
    if (studioRoomId) {
      return reviewStore.studioRoomReviews[studioRoomId];
    }
    return undefined;
  }, [studioRoomId, reviewStore]);
};

/**
 *
 * @Deprecated
 * Please use useUserAccountReviews instead.
 */
export const useUserReviews = (
  artist?: Artist,
  engineer?: Engineer,
  studio?: Studio,
  studioRoom?: StudioRoom,
  args?: {
    studioId?: number;
  },
): ReviewState | undefined => {
  const reviewStore = useAppSelector(selectReviews);

  return useMemo(() => {
    if (engineer && engineer.deleted === null) {
      return reviewStore.engineerReviews[engineer.id];
    } else if (artist) {
      return reviewStore.artistReviews[artist.id];
    } else if ((studio && studio.deleted === null) || args?.studioId) {
      return reviewStore.studioReviews[(studio?.id || args?.studioId)!];
    } else if (studioRoom && studioRoom.deleted === null) {
      return reviewStore.studioRoomReviews[studioRoom.id];
    }
    return undefined;
  }, [engineer, artist, studio, studioRoom, reviewStore, args]);
};

/**
 *
 * @Deprecated
 * Please use useUserAccountPaginatedReviews instead.
 */
export const usePaginatedReviews = (
  artist?: Artist,
  engineer?: Engineer,
  studio?: Studio,
  studioRoom?: StudioRoom,
  orderBy = "created",
): Review[] => {
  const reviewStore = useUserReviews(artist, engineer, studio, studioRoom);

  return useMemo(() => {
    if (!reviewStore) return [];
    const store = reviewStore.recent_reviews;
    const keys: number[] = Object.keys(store).map((k) => +k);
    const initialValue: Review[] = [];
    const reviews: Review[] | undefined = keys.reduce((acc, key) => {
      const pageData: Review[] | undefined = store[key];

      if (pageData) {
        return acc.concat(pageData);
      }
      return acc;
    }, initialValue);
    if (reviews) return reviews;
    return [];
  }, [reviewStore, orderBy]);
};

interface userAccountReviewsProps {
  artistId?: number;
  engineerId?: number;
  studioId?: number;
  studioRoomId?: number;
}

// Functionally equivalent to useUserReviews, but updated params.
// Should use this one for going forward. Better prop handling.
export const useUserAccountReviews = ({
  artistId,
  engineerId,
  studioId,
  studioRoomId,
}: userAccountReviewsProps) => {
  const reviewStore = useAppSelector(selectReviews);

  return useMemo(() => {
    if (engineerId) {
      return reviewStore.engineerReviews[engineerId];
    } else if (artistId) {
      return reviewStore.artistReviews[artistId];
    } else if (studioId) {
      return reviewStore.studioReviews[studioId];
    } else if (studioRoomId) {
      return reviewStore.studioRoomReviews[studioRoomId];
    }
    return undefined;
  }, [
    engineerId,
    artistId,
    studioId,
    studioRoomId,
    reviewStore.engineerReviews,
    reviewStore.artistReviews,
    reviewStore.studioReviews,
    reviewStore.studioRoomReviews,
  ]);
};

// Functionally equivalent to usePaginatedReviews, but updated params.
// Should use this one for going forward. Better prop handling.
// We are reliant on this implementation because we concat the reviews together
// The implementation uses pagination but the result is to append the reviews.
export const useUserAccountPaginatedReviews = ({
  artistId,
  engineerId,
  studioId,
  studioRoomId,
}: userAccountReviewsProps): Review[] => {
  const reviewStore = useUserAccountReviews({
    artistId,
    engineerId,
    studioId,
    studioRoomId,
  });

  return useMemo(() => {
    if (!reviewStore) return [];
    const store = reviewStore.recent_reviews;
    const keys: number[] = Object.keys(store).map((k) => +k);
    const initialValue: Review[] = [];
    const reviews: Review[] | undefined = keys.reduce((acc, key) => {
      const pageData: Review[] | undefined = store[key];

      if (pageData) {
        return acc.concat(pageData);
      }
      return acc;
    }, initialValue);
    if (reviews) return reviews;
    return [];
  }, [reviewStore]);
};
